import { PlusOutlined } from '@ant-design/icons';
import {
  getGetFrontComunicazioniAllegatiIdQueryOptions,
  useDeleteFrontComunicazioniId,
} from '@framework/api-client/api/inquiries-compile/comunicazioni-inalterate/comunicazioni-inalterate.ts';
import {
  getGetFrontRichiesteV2IdComunicazioniQueryKey,
  getGetFrontRichiesteV2IdTipiComunicazioneIdAnteprimaQueryOptions,
  usePostFrontRichiesteV2IdComunicazioni,
  usePostFrontRichiesteV2IdComunicazioniIdInvia,
  usePostFrontRichiesteV2IdTipiComunicazioneIdTipiAllegatoIdAllegati,
} from '@framework/api-client/api/inquiries-compile/comunicazioni-v2/comunicazioni-v2.ts';
import { type GetFrontRichiesteV2IdComunicazioniParams } from '@framework/api-client/api/inquiries-compile/schemas/getFrontRichiesteV2IdComunicazioniParams.ts';
import { type RichiestaDTO } from '@framework/api-client/api/inquiries-compile/schemas/richiestaDTO.ts';
import { type WsRispostaPaginataListComunicazioneDTO } from '@framework/api-client/api/inquiries-compile/schemas/wsRispostaPaginataListComunicazioneDTO.ts';
import { getNetworkErrorMessage } from '@framework/auth/http/errors/getNetworkErrorMessage.ts';
import {
  type CreateCommunicationRequest,
  CreateCommunicationRequestMapper,
  CreateCommunicationRequestSchema,
} from '@framework/common-web/schema/communication/CreateCommunicationRequestSchema.ts';
import { CommunicationButtonDrawer } from '@framework/common-web/ui/communication/CommunicationButtonDrawer.tsx';
import { CommunicationList } from '@framework/common-web/ui/communication/CommunicationList.tsx';
import { CommunicationsContextProvider } from '@framework/common-web/ui/communication/context/CommunicationsContext.tsx';
import { SubmissionLogic } from '@framework/frontoffice-submission/submission/SubmissionLogic.ts';
import { LoaderSpin } from '@framework/ui/components/loader/LoaderSpin.tsx';
import { MessageFilteredList } from '@framework/ui/components/messages/MessageFilteredList.tsx';
import { ColumnSortDirections } from '@framework/ui/components/table/TableColumnSort.tsx';
import { getPagination } from '@framework/ui/core/pagination/getPagination.ts';
import { usePaginationUrlQueryState } from '@framework/ui/core/pagination/paginationUrlQuery.ts';
import { useHistory } from '@framework/ui/core/router/TypedRoute.tsx';
import { Alert, Button, message } from 'antd';
import { isEqual, omit } from 'lodash';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { queryClient } from '../../../core/network/queryClient.ts';
import { useCommunicationList } from './hooks/useCommunicationList.ts';
import { useCommunicationMarkAsRead } from './hooks/useCommunicationMarkAsRead.ts';
import { useFrontofficeCommunicationsTypes } from './useFrontofficeCommunicationTypes.tsx';

export interface ISubmissionSentTabCommunicationsProps {
  active?: boolean;
  submission?: RichiestaDTO;
}

const defaultQuery = getPagination({
  page: 0,
  pageSize: 10,
  direction: ColumnSortDirections.descend,
  sort: 'dataInvio',
});

/**
 * Tab delle comunicazioni post invio di una richiesta richiesta.
 */
export function SubmissionSentTabCommunications(
  props: ISubmissionSentTabCommunicationsProps,
) {
  const { active, submission } = props;
  const history = useHistory();
  const { t } = useTranslation();

  // Salviamo la comunicazione selezionata
  const [selectedCommunication, setSelectedCommunication] =
    useState<CreateCommunicationRequest | null>(null);

  const [query, setQuery] =
    usePaginationUrlQueryState<GetFrontRichiesteV2IdComunicazioniParams>({
      history,
      input: defaultQuery,
    });

  const { records, meta, isFetching, error, isError } = useCommunicationList({
    query,
    selectedCommunication,
    setSelectedCommunication,
    submissionId: submission?.id,
  });

  const isFiltered = useMemo(() => {
    if (isFetching) return false;
    return !isEqual(
      omit(query, ['pagina', 'ordinamenti', 'limite']),
      omit(defaultQuery, ['pagina', 'ordinamenti', 'limite']),
    );
  }, [query, isFetching]);

  // API di aggiornamento e creazione comunicazione
  const upsertCommunication = usePostFrontRichiesteV2IdComunicazioni({
    mutation: {
      onSuccess: async () => {},
    },
  });

  // const previewMutation = usePostBackAttivitaIdComunicazioniAnteprima();

  const sendMutation = usePostFrontRichiesteV2IdComunicazioniIdInvia();
  const sendCommunication = useCallback(
    async (communicationId: string) => {
      if (!submission?.id) {
        // biome-ignore format: <tbd>
        message.error(t('submission-sent-tab-communications.errore-invio-no-id', 'Errore durante l\'invio: Nessun ID trovato per la richiesta.'));
        return;
      }
      const result = await sendMutation.mutateAsync({
        idComunicazione: communicationId,
        idRichiesta: submission.id,
      });
      await queryClient.invalidateQueries(
        getGetFrontRichiesteV2IdComunicazioniQueryKey(submission.id),
      );
      // Chiudiamo il drawer dopo l'invio
      setSelectedCommunication(null);

      return result;
    },
    [sendMutation, submission, t],
  );

  /**
   * Gestisce il salvataggio o l'invio comunicazione lato frontoffice
   */
  const handleSave = async (
    communicationRequest: CreateCommunicationRequest,
    manual: boolean | null | undefined,
    options?: {
      saveOnly?: boolean | null;
    },
  ) => {
    const submissionId = submission?.id;

    if (!submissionId) {
      // biome-ignore format: <tbd>
      message.error(t('submission-sent-tab-communications.no-submission-id-error-message', `Errore durante il salvataggio: Nessun ID trovato per la richiesta.`));
      return;
    }

    const isNew = selectedCommunication?.idComunicazione == null;
    try {
      const result = await upsertCommunication.mutateAsync({
        idRichiesta: submissionId,
        data: CreateCommunicationRequestMapper.formDataToRequest(
          communicationRequest,
        ),
      });

      if (options?.saveOnly) {
        return result;
      }

      if (isNew) {
        // biome-ignore format: <tbd>
        message.success(t('submission-sent-tab-communications.comunicazione-modificata-con-successo', 'Comunicazione modificata con successo'));
      } else {
        // biome-ignore format: <tbd>
        message.success(t( 'submission-sent-tab-communications.comunicazione-creata-con-successo', 'Comunicazione creata con successo'));
      }

      await queryClient.invalidateQueries({
        predicate: ({ queryKey }) =>
          queryKey[0] ===
          getGetFrontRichiesteV2IdComunicazioniQueryKey(submissionId)[0],
      });

      const allCommunications =
        queryClient.getQueriesData<WsRispostaPaginataListComunicazioneDTO>(
          getGetFrontRichiesteV2IdComunicazioniQueryKey(submissionId),
        )[0];

      const communication = allCommunications?.[1]?.dati?.find(
        c => c.id === result?.id,
      );

      setSelectedCommunication(
        communication
          ? CreateCommunicationRequestMapper.requestToFormData(communication)
          : null,
      );

      setQuery({ ...defaultQuery });
      return result;
    } catch (error) {
      console.error(error);
      if (isNew) {
        // biome-ignore format: <tbd>
        message.error(getNetworkErrorMessage(error, t('submission-sent-tab-communications.errore-modifica-comunicazione', 'Impossibile modificare la comunicazione')));
      } else {
        // biome-ignore format: <tbd>
        message.error(getNetworkErrorMessage(error, t( 'submission-sent-tab-communications.errore-creazione-comunicazione', 'Impossibile creare la comunicazione')));
      }
    }
  };

  // Allegati
  const uploadMutation =
    usePostFrontRichiesteV2IdTipiComunicazioneIdTipiAllegatoIdAllegati({});
  const uploadAttachment = useCallback(
    async (
      communicationTypeId: string,
      attachmentGroupId: string,
      file: Blob,
    ) => {
      try {
        const result = await uploadMutation.mutateAsync({
          idRichiesta: submission!.id!,
          idTipoComunicazione: communicationTypeId,
          idTipoAllegato: attachmentGroupId,
          data: { file },
        });
        queryClient.invalidateQueries(
          getGetFrontRichiesteV2IdComunicazioniQueryKey(submission!.id!),
        );
        return result;
      } catch (error) {
        message.error(
          getNetworkErrorMessage(
            error,
            t(
              'submission-sent-tab-communications.errore-caricamento-allegato',
              "Impossibile caricare l'allegato",
            ),
          ),
        );
      }
    },
    [submission, t, uploadMutation],
  );

  const deleteCommunicationMutation = useDeleteFrontComunicazioniId({
    mutation: {
      onSuccess: async () => {
        message.success(
          t(
            'submission-sent-tab-communications.comunicazione-eliminata-con-successo',
            'Comunicazione eliminata con successo',
          ),
        );
        await queryClient.invalidateQueries(
          getGetFrontRichiesteV2IdComunicazioniQueryKey(submission!.id!),
        );
      },
      onError: error => {
        message.error(
          getNetworkErrorMessage(
            error,
            t(
              'submission-sent-tab-communications.errore-eliminazione-comunicazione',
              'Impossibile eliminare la comunicaizone',
            ),
          ),
        );
      },
    },
  });

  const handleDeleteDraft = useCallback(
    async (communicationId: string) => {
      await deleteCommunicationMutation.mutateAsync({ id: communicationId });
    },
    [deleteCommunicationMutation],
  );

  // Presa lettura di una comunicazione ricevuta
  const markCommunicationAsRead = useCommunicationMarkAsRead({
    submissionId: submission?.id,
  });

  // Tipi comunicazione ricercabili
  const { searchableTypes, sendableTypes, receivableTypes } =
    useFrontofficeCommunicationsTypes(submission?.id);

  if (!active) return null;

  if (isError) {
    return (
      <Alert
        showIcon
        type="error"
        // biome-ignore format: <tbd>
        message={t('submission-sent-tab-communications.errore-caricamento-comunicazioni', 'Impossibile caricare le comunicazioni')}
        // biome-ignore format: <tbd>
        description={getNetworkErrorMessage( error, t('submission-sent-tab-communications.errore-tab-comunicazioni', 'Si è verificato un errore'))}
      />
    );
  }

  return (
    <div>
      <CommunicationsContextProvider
        submissionId={submission?.id}
        query={query}
        setQuery={setQuery}
        searchableTypes={searchableTypes}
        sendableTypes={sendableTypes}
        // Nel Frontoffice non esiste il caricamento manuale: i tipi
        // ricevibili valgono come caricabili
        loadableTypes={receivableTypes}
        // Tutte le comunicazioni inviabili sono anche modificabili
        editableTypes={sendableTypes}
        parentProcedureId={null}
        isProcedureOpen={null}
        onDelete={handleDeleteDraft}
        onEdit={communication =>
          setSelectedCommunication(
            CreateCommunicationRequestMapper.requestToFormData(communication),
          )
        }
        isEditable={SubmissionLogic.canSendCommunications(submission?.stato)}
        getDownloadAttachmentQueryOptions={
          getGetFrontComunicazioniAllegatiIdQueryOptions
        }
        onSave={handleSave}
        uploadAttachment={uploadAttachment}
        onSend={sendCommunication}
        onMarkAsRead={markCommunicationAsRead}
        // FO non ci sono
        procedureId={undefined}
        taskId={undefined}
        attachExisting={undefined}
        getHydrateCommunicationQueryOptions={
          getGetFrontRichiesteV2IdTipiComunicazioneIdAnteprimaQueryOptions
        }
        isBackoffice={false}
      >
        <MessageFilteredList
          style={{ textAlign: 'left' }}
          visible={isFiltered && !isFetching}
          items={meta?.totale ?? null}
          message={t(
            'submission-sent-tab-communications.comunicazioni-trovate-in-base-ai-filtri',
            'comunicazioni trovate in base ai filtri di ricerca.',
          )}
          action={
            <Button size="small" onClick={() => setQuery({})}>
              {t(
                'submission-sent-tab-communications.rimuovi-filtri',
                'Rimuovi filtri',
              )}
            </Button>
          }
        />

        <LoaderSpin spinning={isFetching} hasFullWidthChildren noPadding>
          <CommunicationList
            defaultQuery={defaultQuery}
            query={query}
            setQuery={setQuery}
            total={meta?.totale ?? 0}
            communications={records ?? []}
            extra={
              SubmissionLogic.canSendCommunications(submission?.stato) &&
              (sendableTypes?.length ?? 0) > 0 && (
                <Button
                  icon={<PlusOutlined aria-hidden="true" />}
                  size={'large'}
                  type={'default'}
                  onClick={() =>
                    setSelectedCommunication(
                      CreateCommunicationRequestSchema.cast(
                        {},
                        { assert: false },
                      ),
                    )
                  }
                >
                  {t(
                    'submission-sent-tab-communications.nuova-comunicazione',
                    'Nuova Comunicazione',
                  )}
                </Button>
              )
            }
          />
        </LoaderSpin>
        <CommunicationButtonDrawer
          communication={selectedCommunication}
          onClose={() => setSelectedCommunication(null)}
          validationSchema={CreateCommunicationRequestSchema}
        />
      </CommunicationsContextProvider>
    </div>
  );
}
